import { v4 as uuidv4 } from "uuid";
import { WEEKDAYS } from "@/pages/Settings/DepartmentConfiguration/utils";
import { RATIO_LADDER_UNBOUNDED_CENSUS_END } from "@/utils/censusResolution";
import { type NurseFullRole, NurseFullRoleShort, NurseRole, type RatioLadder } from "@/utils/models";
import { weekdayName } from "./dayToggleCells";

/**
 * The ratio-ladder editor's rules. A ratio location is staffed ONLY by its ladder, so the ladder has to
 * say how many staff it needs at every census from 0 to ∞ on every day it runs. Pure functions here;
 * the popup hook maps each result to text.
 */

/** Which staffing dimension the ladder's rows are keyed by: the location's job, or each of its roles. */
export type LadderDimension = { kind: "job" } | { kind: "roles"; roles: readonly NurseRole[] };

export type LadderRules = {
	dimension: LadderDimension;
	/** Every row must name a job (job-managed departments with more than one job). */
	requireJobPerRow: boolean;
	/** Every row must name at least one role (role-based departments with more than one role). */
	requireRolesPerRow: boolean;
};

/** A census range no row covers, on these days, for this role (`null` when the ladder is keyed by job). */
export type LadderCoverageGap = { start: number; end: number; days: string[]; role: NurseRole | null };

export type RatioLadderProblem =
	| { kind: "empty" }
	| { kind: "job" }
	| { kind: "roles" }
	| { kind: "amount" }
	| { kind: "days" }
	| { kind: "range" }
	| { kind: "overlap" }
	| { kind: "gap"; gap: LadderCoverageGap };

/** Who a new row belongs to: the location's roles and job. */
export type LadderRowOwner = { roles: NurseRole[]; jobId: string };

// DEFAULT: a row saved without `days` applies to every weekday — the backend model's default and the
// canonical resolver's `rungAppliesOnDay` reading. The editor always writes days explicitly.
export const rowDays = (row: RatioLadder): readonly string[] => row.days ?? WEEKDAYS;

/** Job-less rows carry "" or no jobId depending on where they came from; both mean "no job". */
const rowJobId = (row: RatioLadder): string => row.jobId ?? "";

/**
 * Whether two rows staff the same thing: the same job, or — when neither has a job — a shared role. Rows
 * with neither yet (a new location before its job or roles are picked) all belong to that one location
 * and are given the same job or roles once it is picked, so they count as the same too.
 */
const sameLadderDimension = (a: RatioLadder, b: RatioLadder): boolean => {
	if (rowJobId(a) !== rowJobId(b)) return false;
	if (rowJobId(a) !== "") return true;
	if (a.roles.length === 0 && b.roles.length === 0) return true;
	return a.roles.some((role) => b.roles.includes(role));
};

/** The location's roles as ladder rows name them — every department role when the location takes "all". */
export const toLocationRoles = (roles: readonly (NurseFullRole | "all")[], departmentRoles: NurseRole[]): NurseRole[] => {
	if (roles.includes("all")) return departmentRoles;
	return roles.filter((role): role is NurseFullRole => role !== "all").map((role) => NurseRole[NurseFullRoleShort[role as keyof typeof NurseFullRoleShort]]); // the enum's keys ARE the full-role values; TS can't see that
};

/** A whole-number Min of 0 or more, and a Max no lower than it and no higher than ∞. */
export const hasValidRange = (row: RatioLadder): boolean =>
	Number.isInteger(row.censusStart) &&
	Number.isInteger(row.censusEnd) &&
	row.censusStart >= 0 &&
	row.censusEnd >= row.censusStart &&
	row.censusEnd <= RATIO_LADDER_UNBOUNDED_CENSUS_END;

/**
 * Returns a map of entryId → set of conflicting day abbreviations. A row with a malformed range is
 * reported as a range problem instead, so it is not also painted as overlapping every other row.
 */
export const getConflictingDaysByRow = (entries: RatioLadder[]): Map<string, Set<string>> => {
	const result = new Map<string, Set<string>>();
	for (let i = 0; i < entries.length; i++) {
		for (let j = i + 1; j < entries.length; j++) {
			const a = entries[i];
			const b = entries[j];
			if (!sameLadderDimension(a, b) || !hasValidRange(a) || !hasValidRange(b)) continue;
			if (a.censusStart > b.censusEnd || b.censusStart > a.censusEnd) continue;
			const bDays = new Set(rowDays(b));
			const sharedDays = rowDays(a).filter((d) => bDays.has(d));
			if (sharedDays.length === 0) continue;
			const aSet = result.get(a.id) ?? new Set<string>();
			const bSet = result.get(b.id) ?? new Set<string>();
			for (const d of sharedDays) {
				aSet.add(d);
				bSet.add(d);
			}
			result.set(a.id, aSet);
			result.set(b.id, bSet);
		}
	}
	return result;
};

/** The census ranges one day's rows leave uncovered, walking from 0 up to ∞. */
export const uncoveredRanges = (rows: readonly RatioLadder[]): Array<{ start: number; end: number }> => {
	const gaps: Array<{ start: number; end: number }> = [];
	let covered = 0;
	for (const row of [...rows].sort((a, b) => a.censusStart - b.censusStart)) {
		if (row.censusStart > covered) gaps.push({ start: covered, end: row.censusStart - 1 });
		covered = Math.max(covered, row.censusEnd + 1);
	}
	if (covered <= RATIO_LADDER_UNBOUNDED_CENSUS_END) gaps.push({ start: covered, end: RATIO_LADDER_UNBOUNDED_CENSUS_END });
	return gaps;
};

/** The groups of rows that must each cover 0–∞ on their own, with the role they answer for. */
const dimensionGroups = (ladder: readonly RatioLadder[], dimension: LadderDimension): Array<{ role: NurseRole | null; rows: RatioLadder[] }> => {
	if (dimension.kind === "roles") {
		return dimension.roles.map((role) => ({ role, rows: ladder.filter((row) => row.roles.includes(role)) }));
	}
	const jobIds = [...new Set(ladder.map(rowJobId))];
	return jobIds.map((jobId) => ({ role: null, rows: ladder.filter((row) => rowJobId(row) === jobId) }));
};

/**
 * Every census range the ladder leaves without a staffing answer. A day with no rows for a role (or
 * job) is not a gap: the location simply is not staffed for it that day. Days sharing the same missing
 * range come back as one gap, in week order. Assumes every row's range is valid (see
 * `findRatioLadderProblem`, which checks that first).
 */
export const findRatioLadderCoverageGaps = (ladder: readonly RatioLadder[], dimension: LadderDimension): LadderCoverageGap[] => {
	const gaps: LadderCoverageGap[] = [];
	for (const { role, rows } of dimensionGroups(ladder, dimension)) {
		for (const day of WEEKDAYS) {
			const dayRows = rows.filter((row) => rowDays(row).includes(day));
			if (dayRows.length === 0) continue;
			for (const range of uncoveredRanges(dayRows)) {
				const same = gaps.find((gap) => gap.role === role && gap.start === range.start && gap.end === range.end);
				if (same) same.days.push(day);
				else gaps.push({ ...range, days: [day], role });
			}
		}
	}
	return gaps;
};

/** A whole number of staff, 0 or more: 0 says the range needs no one, a blank (NaN) says nothing. */
export const hasValidAmount = (row: RatioLadder): boolean => Number.isInteger(row.amount) && row.amount >= 0;

const rowProblem = (row: RatioLadder, rules: LadderRules): RatioLadderProblem | null => {
	if (rules.requireJobPerRow && !row.jobId) return { kind: "job" };
	if (rules.requireRolesPerRow && row.roles.length === 0) return { kind: "roles" };
	if (!hasValidAmount(row)) return { kind: "amount" };
	if (rowDays(row).length === 0) return { kind: "days" };
	if (!hasValidRange(row)) return { kind: "range" };
	return null;
};

/**
 * The first reason this ladder cannot be saved, or `null` when it is complete. Row-level problems come
 * first, then overlaps, then gaps: a gap is only meaningful once every range is well-formed and no two
 * rows claim the same census.
 */
export const findRatioLadderProblem = (ladder: RatioLadder[], rules: LadderRules): RatioLadderProblem | null => {
	if (ladder.length === 0) return { kind: "empty" };
	for (const row of ladder) {
		const problem = rowProblem(row, rules);
		if (problem) return problem;
	}
	if (getConflictingDaysByRow(ladder).size > 0) return { kind: "overlap" };
	const [firstGap] = findRatioLadderCoverageGaps(ladder, rules.dimension);
	return firstGap ? { kind: "gap", gap: firstGap } : null;
};

const openEndedRow = (owner: LadderRowOwner, fields: Pick<RatioLadder, "censusStart" | "amount" | "days">): RatioLadder => ({
	id: uuidv4(),
	location_id: "",
	roles: [...owner.roles],
	jobId: owner.jobId,
	censusEnd: RATIO_LADDER_UNBOUNDED_CENSUS_END,
	...fields,
});

/**
 * The starting ladder for a ratio location that has none: each staffing-target row becomes a 0–∞ row
 * with the same days and amount, so the admin starts from the numbers they configured. A new location's
 * single "every day, 1" target row makes the default 0–∞ × 1 ladder.
 */
export const seedRatioLadderFromTargets = (targetRows: ReadonlyArray<{ days: string[]; amount: number }>, owner: LadderRowOwner): RatioLadder[] =>
	targetRows.filter((target) => target.days.length > 0).map((target) => openEndedRow(owner, { censusStart: 0, amount: target.amount, days: [...target.days] }));

/** The open-ended row that starts highest — the one a new row is split off. */
const topOpenEndedRow = (ladder: readonly RatioLadder[]): RatioLadder | undefined =>
	ladder
		.filter((row) => row.censusEnd === RATIO_LADDER_UNBOUNDED_CENSUS_END)
		.reduce<RatioLadder | undefined>((best, row) => (best === undefined || row.censusStart >= best.censusStart ? row : best), undefined);

/** Whether a row can be added: not once the open-ended row starts at ∞, which leaves no census to split off. */
export const canAddLadderRow = (ladder: readonly RatioLadder[]): boolean => {
	const top = topOpenEndedRow(ladder);
	return top === undefined || top.censusStart < RATIO_LADDER_UNBOUNDED_CENSUS_END;
};

/**
 * Add a row without opening a gap. The open-ended row that starts highest is split: it keeps its Min
 * and ends there, and the new row takes over from the next census up to ∞, one staff member more.
 * With no open-ended row yet, the new row starts after the highest Max and runs to ∞. Only call it when
 * `canAddLadderRow` allows.
 */
export const addLadderRow = (ladder: RatioLadder[], owner: LadderRowOwner): { ladder: RatioLadder[]; newRowId: string } => {
	if (!canAddLadderRow(ladder)) throw new Error("The ratio ladder has no census left to add a row for");
	const top = topOpenEndedRow(ladder);
	if (top) {
		const added = openEndedRow(
			{ roles: top.roles, jobId: rowJobId(top) },
			{ censusStart: top.censusStart + 1, amount: top.amount + 1, days: [...rowDays(top)] },
		);
		const split = ladder.map((row) => (row.id === top.id ? { ...row, censusEnd: row.censusStart } : row));
		return { ladder: [...split, added], newRowId: added.id };
	}
	const highestMax = ladder.length > 0 ? Math.max(...ladder.map((row) => row.censusEnd)) : -1;
	const highestAmount = ladder.length > 0 ? Math.max(...ladder.map((row) => row.amount)) : 0;
	const added = openEndedRow(owner, { censusStart: highestMax + 1, amount: highestAmount + 1, days: [...WEEKDAYS] });
	return { ladder: [...ladder, added], newRowId: added.id };
};

/**
 * Set a row's Max. Rows that started right after it (same job or role, running only on days it runs)
 * move their Min along, so resizing a range never opens a gap — unless that would push their Min past
 * their own Max, which is left as an overlap for the admin to resolve. A row that also runs on days this
 * one doesn't stays put: on those days it follows another step, which moving it would break. A Max at
 * or above the unbounded value is ∞.
 *
 * Followers are found by the row's Max in `ladder`, so while the admin types, pass the ladder as it was
 * when the edit began: every keystroke then starts from the same rows, and an intermediate value (the
 * "3" on the way to "30", or a cleared field) leaves nothing behind.
 */
export const setLadderRowMax = (ladder: RatioLadder[], rowId: string, newMax: number): RatioLadder[] => {
	const target = ladder.find((row) => row.id === rowId);
	if (!target) throw new Error(`Ratio ladder row ${rowId} is not in the ladder`);
	const max = newMax >= RATIO_LADDER_UNBOUNDED_CENSUS_END ? RATIO_LADDER_UNBOUNDED_CENSUS_END : newMax;
	const followsTarget = (row: RatioLadder): boolean =>
		row.id !== target.id &&
		Number.isInteger(target.censusEnd) &&
		Number.isInteger(max) &&
		row.censusStart === target.censusEnd + 1 &&
		max + 1 <= row.censusEnd &&
		sameLadderDimension(row, target) &&
		rowDays(row).length > 0 &&
		rowDays(row).every((day) => rowDays(target).includes(day));
	return ladder.map((row) => {
		if (row.id === target.id) return { ...row, censusEnd: max };
		return followsTarget(row) ? { ...row, censusStart: max + 1 } : row;
	});
};

/** Locale-short weekday names for ladder days, in the order given, e.g. ["mon", "tue"] → "Mon, Tue". Throws on a value that isn't a stored weekday key. */
export const formatLadderDays = (days: readonly string[]): string => days.map((day) => weekdayName(day, "short")).join(", ");
